// Issues tab helpers — saved views, filters, sort, grouping, Helm priority order (parity with IssuesView.swift).
// Everything here is pure and client-side: the server already returns the whole open list per instance.
import type { Issue } from './types';
import { parseDate } from './format';

export type SavedView = 'triage' | 'mine' | 'open';
export const SAVED_VIEWS: { id: SavedView; label: string }[] = [
  { id: 'triage', label: 'Needs triage' }, { id: 'mine', label: 'Mine' }, { id: 'open', label: 'All open' },
];

export type SortKey = 'priority' | 'severity' | 'age' | 'updated' | 'key' | 'status';
export const SORTS: { id: SortKey; label: string; short: string }[] = [
  { id: 'priority', label: 'Priority (Helm order)', short: 'Priority' },
  { id: 'severity', label: 'Severity', short: 'Severity' },
  { id: 'age', label: 'Age (oldest first)', short: 'Age' },
  { id: 'updated', label: 'Updated', short: 'Updated' },
  { id: 'key', label: 'Key', short: 'Key' },
  { id: 'status', label: 'Status', short: 'Status' },
];

export type AgeBucket = 'any' | 'day' | 'week' | 'month' | 'old';
export const AGE_BUCKETS: { id: AgeBucket; label: string }[] = [
  { id: 'any', label: 'Any age' }, { id: 'day', label: 'Under 1 day' }, { id: 'week', label: '1–7 days' },
  { id: 'month', label: '7–30 days' }, { id: 'old', label: 'Over 30 days' },
];

export const STATUS_ORDER = ['new', 'acknowledged', 'in_progress', 'code_complete', 'waiting_customer', 'waiting_parts', 'planned', 'backlog'];
export const DONE_STATUSES = ['resolved', 'closed', 'wont_fix', 'superseded'];
export const STATUS_LABEL: Record<string, string> = {
  new: 'New — needs triage', acknowledged: 'Acknowledged', in_progress: 'In progress',
  code_complete: 'Code complete — needs testing', waiting_customer: 'Waiting on AquaStar', waiting_parts: 'Waiting on parts',
  planned: 'Planned', backlog: 'Backlog', resolved: 'Resolved', closed: 'Closed', wont_fix: "Won't fix", superseded: 'Superseded',
};
export const SEVERITIES = ['critical', 'high', 'medium', 'low'];
export const ISSUE_TYPES = ['bug', 'enhancement', 'feature_request', 'connectivity', 'pump_fault', 'data_telemetry', 'how_to', 'general', 'roadmap_task', 'billing'];
const SEV_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

export interface Filters { status: string; severity: string; type: string; app: string; age: AgeBucket; tag: string }
export const NO_FILTERS: Filters = { status: 'any', severity: 'any', type: 'any', app: 'any', age: 'any', tag: 'any' };
export const activeFilterCount = (f: Filters): number =>
  [f.status !== 'any', f.severity !== 'any', f.type !== 'any', f.app !== 'any', f.age !== 'any', f.tag !== 'any'].filter(Boolean).length;

export const statusText = (i: Issue): string => STATUS_LABEL[i.status ?? ''] ?? i.statusName ?? i.status ?? '?';
export const tagsOf = (i: Issue): string[] => i.helmTags ?? [];

export function ageDays(i: Issue, now: number = Date.now()): number | null {
  const t = parseDate(i.createdAt ?? i.updatedAt);
  return t == null ? null : (now - t) / 86400000;
}
export function ageInBucket(days: number | null, b: AgeBucket): boolean {
  if (b === 'any') return true;
  if (days == null) return false;
  switch (b) {
    case 'day': return days < 1;
    case 'week': return days >= 1 && days < 7;
    case 'month': return days >= 7 && days < 30;
    case 'old': return days >= 30;
  }
}

export interface Who { id?: string | null; name?: string | null }
/** "Mine" = the tracker's assignee (name or id) is the signed-in user. Empty until a tracker returns assignees. */
export function isMine(i: Issue, me: Who | null): boolean {
  const a = (i.assignee ?? '').toLowerCase();
  if (!a) return false;
  return a === 'me' || a === (me?.id ?? '').toLowerCase() || (!!me?.name && a === me.name.toLowerCase());
}

export interface MatchCtx { query: string; view: SavedView; filters: Filters; me: Who | null; now?: number }
export function matchesIssue(i: Issue, c: MatchCtx): boolean {
  const q = c.query.trim().toLowerCase();
  if (q && !(i.title.toLowerCase().includes(q) || i.key.toLowerCase().includes(q) || (i.app ?? '').toLowerCase().includes(q))) return false;
  switch (c.view) {
    case 'triage': if (i.status !== 'new') return false; break;
    case 'mine': if (!isMine(i, c.me)) return false; break;
    case 'open': if (DONE_STATUSES.includes(i.status ?? '')) return false; break;
  }
  const f = c.filters;
  if (f.status !== 'any' && i.status !== f.status) return false;
  if (f.severity !== 'any' && i.severity !== f.severity) return false;
  if (f.type !== 'any' && i.type !== f.type) return false;
  if (f.app !== 'any' && i.app !== f.app) return false;
  if (f.tag === 'none') { if (tagsOf(i).length) return false; }
  else if (f.tag !== 'any' && !tagsOf(i).some((t) => t.toLowerCase() === f.tag.toLowerCase())) return false;
  return ageInBucket(ageDays(i, c.now), f.age);
}

const sev = (i: Issue): number => SEV_RANK[i.severity ?? ''] ?? 9;
const upd = (i: Issue): number => parseDate(i.updatedAt) ?? -Infinity;
const created = (i: Issue): number => parseDate(i.createdAt ?? i.updatedAt) ?? Infinity;
export const issueNumber = (i: Issue): number => i.number ?? (parseInt(i.key.split('-').pop() ?? '', 10) || 0);

export function sortIssues(rows: Issue[], by: SortKey): Issue[] {
  const cmp = (a: Issue, b: Issue): number => {
    switch (by) {
      case 'priority': {
        // Helm's ranked order first (rank ascending), then the tracker's severity / recency.
        const ra = a.helmRank ?? Infinity, rb = b.helmRank ?? Infinity;
        if (ra !== rb) return ra < rb ? -1 : 1;
        return sev(a) !== sev(b) ? sev(a) - sev(b) : upd(b) - upd(a);
      }
      case 'severity': return sev(a) !== sev(b) ? sev(a) - sev(b) : upd(b) - upd(a);
      case 'age': return created(a) !== created(b) ? created(a) - created(b) : issueNumber(a) - issueNumber(b);
      case 'updated': return upd(b) - upd(a);
      case 'key': case 'status':
        return issueNumber(a) !== issueNumber(b) ? issueNumber(a) - issueNumber(b) : a.key.localeCompare(b.key);
    }
  };
  return rows.map((r, n) => [r, n] as const).sort((x, y) => cmp(x[0], y[0]) || x[1] - y[1]).map((x) => x[0]);   // stable
}

export interface StatusGroup { status: string; label: string; rows: Issue[] }
/** Rows grouped by status: the known statuses in workflow order, then any others alphabetically. */
export function groupByStatus(rows: Issue[]): StatusGroup[] {
  const by = new Map<string, Issue[]>();
  for (const r of rows) { const k = r.status ?? 'unknown'; (by.get(k) ?? by.set(k, []).get(k)!).push(r); }
  const known = STATUS_ORDER.filter((s) => by.has(s));
  const others = [...by.keys()].filter((s) => !STATUS_ORDER.includes(s)).sort();
  return [...known, ...others].map((s) => ({ status: s, label: STATUS_LABEL[s] ?? by.get(s)![0].statusName ?? s, rows: by.get(s)! }));
}

// ---- Helm priority order -----------------------------------------------------------------------------------------

/** The open set of an instance in Helm order (ranked first, then severity / recency) — what Prioritize edits. */
export function openSet(rows: Issue[]): Issue[] {
  return sortIssues(rows.filter((r) => !DONE_STATUSES.includes(r.status ?? '')), 'priority');
}

export interface Reordered { order: Issue[]; keys: string[] }
/**
 * Move one item of the open set to `dest` (final index) and compute what to persist: every issue up to the furthest
 * position the move touched (and everything already ranked) gets a rank; the rest stay unranked and keep the tracker's order.
 */
export function reorder(items: Issue[], from: number, dest: number): Reordered | null {
  if (from < 0 || from >= items.length) return null;
  const to = Math.max(0, Math.min(items.length - 1, dest));
  if (to === from) return null;
  const order = items.slice();
  const [m] = order.splice(from, 1);
  order.splice(to, 0, m);
  const touched = Math.max(from, to);
  const ranked = items.filter((i) => i.helmRank != null).length;
  const count = Math.min(order.length, Math.max(ranked, touched + 1));
  return { order, keys: order.slice(0, count).map((i) => i.key) };
}
export function moveBy(items: Issue[], key: string, delta: number): Reordered | null {
  const idx = items.findIndex((i) => i.key === key);
  return idx < 0 ? null : reorder(items, idx, idx + delta);
}
export function moveToTop(items: Issue[], key: string): Reordered | null {
  const idx = items.findIndex((i) => i.key === key);
  return idx <= 0 ? null : reorder(items, idx, 0);
}
/** Apply a persisted order to a row list locally (optimistic): ranks 0..n for these keys, all others unranked. */
export function applyRanks(rows: Issue[], orderedKeys: string[]): Issue[] {
  const rank = new Map(orderedKeys.map((k, n) => [k.toUpperCase(), n] as const));
  return rows.map((r) => ({ ...r, helmRank: rank.has(r.key.toUpperCase()) ? rank.get(r.key.toUpperCase())! : null }));
}

/** Toggle a tag case-insensitively on a tag list (add if absent, remove if present). */
export function toggleTag(tags: string[], name: string): string[] {
  const i = tags.findIndex((t) => t.toLowerCase() === name.toLowerCase());
  return i >= 0 ? tags.filter((_, n) => n !== i) : [...tags, name];
}

/** Stable colour for a tag with no palette colour (hash of the lower-cased name -> one of 8 hues). */
const TAG_HUES = ['#4c8dff', '#3ecf8e', '#ff9f43', '#a78bfa', '#f472b6', '#2dd4bf', '#818cf8', '#c08457'];
export function tagColor(name: string, palette: { name: string; color?: string | null }[]): string {
  const hex = palette.find((t) => t.name.toLowerCase() === name.toLowerCase())?.color;
  if (hex && /^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  let sum = 0;
  for (const ch of name.toLowerCase()) sum = (sum * 31 + ch.codePointAt(0)!) & 0xffff;
  return TAG_HUES[sum % TAG_HUES.length];
}
